import React, { FC, useState, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  Table,
  Button,
  StatusTip,
  notification,
  Bubble,
} from 'tea-component';
import type { TableColumn } from 'tea-component';
import GameImage from '@/pages/home/components/GameImage';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { constant, result, startCase } from 'lodash-es';
import { BenchmarkGameItem, SegmentOption } from '../../types';
import { getDetailGameOverviewPath } from '@/pages/intelligence/utils/topchartsUtils';
import { followingCollectionGames } from '@intelligence/services/pcConsoleBenchmark';
import { useIntl, history, getLocale } from 'umi';
import classnames from 'classnames';
import { uniq } from 'lodash-es';

import './index.scss';

const { Footer, Body } = Modal;
const { scrollable } = Table.addons;
const { LoadingTip } = StatusTip;

interface GameType {
  platform?: ('mobile' | 'pc' | 'console')[];
  company: string;
  company_country: string;
  entity_name: string;
  entity_type: 'mobile' | 'pc' | 'console';
  cover: string;
  id: string;
  pc_id: string;
  console_id: string;
}

interface GroupType {
  entity_name: string;
  entity_type: string;
  game_list: any[] | null;
}

interface FollowGameProps {
  className?: string;
  loading: boolean;
  gameList: BenchmarkGameItem[];
  genre: string;
  entity_type: 'mobile' | 'combine';
  genreList: SegmentOption<string>[];
  groupList: GroupType[];
  getCollectionGroupList: ()=> void;
}

interface genreGameMapType {
  newGame: GameType[];
  followedGame: GameType[];
}

const GAME_ICON_MAP = {
  mobile: 'other-icon',
  pc: 'monitor',
  console: 'game',
};

const FollowGame: React.FC<FollowGameProps> = (props) => {
  const {
    className,
    gameList,
    genre,
    entity_type,
    genreList,
    groupList = [],
    loading = false,
    getCollectionGroupList
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const followedGamesIds = useRef<{
    mobile: string[];
    pc: string[];
    console: string[];
  }>({
    mobile: [],
    pc: [],
    console: [],
  });
  const [visible, setVisible] = useState(false);
  const [hasGroupName, setHasGroupName] = useState(false);
  const [hasSameGame, setHasSameSame] = useState(false);
  const [genreGameMap, setGenreGameMap] = useState<genreGameMapType>({
    newGame: [],
    followedGame: [],
  });

  const dataSource = useMemo(() => {
    if (!Array.isArray(gameList)) return [];

    if (hasGroupName && !hasSameGame) {
      let recordsList = [];
      if (genreGameMap?.newGame?.length) {
        recordsList.push({
          id: null,
          cover: '',
          entity_name: '',
          rank: formatMessage({ id: 'dbi_benchmark_new' }),
          platform: [],
          pc_id: '',
          console_id: '',
        });
        const newGameList = genreGameMap?.newGame?.map((item, index) => {
          return {
            id: item.id,
            cover: item?.cover,
            entity_name: item?.entity_name,
            rank: index + 1,
            platform: item?.platform || ['mobile'],
            pc_id: item?.pc_id || '',
            console_id: item?.console_id || '',
          };
        });

        recordsList.push(...newGameList);
      }

      if (genreGameMap?.followedGame?.length) {
        recordsList.push({
          id: null,
          cover: '',
          entity_name: '',
          rank: formatMessage({ id: 'dbi_benchmark_followed' }),
          platform: [],
        });
        const followedGameList = genreGameMap?.followedGame?.map(
          (item, index) => {
            return {
              id: item.id || item.app_id,
              cover: item?.cover,
              entity_name: item?.entity_name || item?.name,
              rank: index + 1,
              platform: item?.platform || ['mobile'],
              pc_id: item?.pc_id || '',
              console_id: item?.console_id || '',
            };
          },
        );

        recordsList.push(...followedGameList);
      }

      return recordsList;
    }

    return gameList?.map((item, index) => {
      return {
        id: item.id,
        cover: item?.cover,
        entity_name: item?.entity_name,
        rank: index + 1,
        platform: item?.platform || ['mobile'],
        pc_id: item?.pc_id || '',
        console_id: item?.console_id || '',
      };
    });
  }, [gameList, hasGroupName, hasSameGame, genreGameMap, locale]);

  const genreConfig = useMemo(() => {
    if (!Array.isArray(genreList)) return { name: '', title: '' };
    const genreGame = genreList.find((item) => item?.value === genre);

    if (genreGame) return { name: genreGame?.name || '', text: genreGame.text };
    return { name: '', title: '' };
  }, [genreList, genre]);

  const entityName = useMemo(() => {
    if (entity_type === 'combine') {
      return 'PC&Console';
    }
    return startCase(entity_type);
  }, [entity_type]);

  useEffect(() => {
    const targetGroupList = groupList?.filter(
      (item) => item?.entity_name === `${entityName} - ${genreConfig?.name}`,
    );

    // 存在对应的分组
    if (!targetGroupList?.length) {
      setHasGroupName(false);
      followedGamesIds.current.mobile = [];
      followedGamesIds.current.pc = [];
      followedGamesIds.current.console = [];
    } else {
      if (entity_type === 'mobile') {
        const followGameList =
          targetGroupList?.find((item) => item.entity_type === 'mobile')
            ?.game_list || [];
        const { newGame, followedGame } = diffGameResult(
          gameList,
          followGameList,
        );
        if (newGame.length > 0) {
          setHasSameSame(false);
        } else {
          if (
            !!(
              followedGame?.length > 0 &&
              // followedGame?.length <= gameList?.length
              followedGame?.length <= followGameList?.length
            )
          ) {
            setHasSameSame(true);
          }else{
            setHasSameSame(false);
          }
         
        }

        setGenreGameMap({ newGame, followedGame });
        setHasGroupName(true);
        followedGamesIds.current.mobile = followGameList?.map((item) => item?.app_id) || [];
        followedGamesIds.current.pc = [];
        followedGamesIds.current.console = [];
      }

      if (entity_type === 'combine') {
        const pcGameList =
          targetGroupList?.find((item) => item.entity_type === 'pc')
            ?.game_list || [];
        const consoleGameList =
          targetGroupList?.find((item) => item.entity_type === 'console')
            ?.game_list || [];
        // 找pc的new 已关注过的游戏
        const pcConfig = diffGameResult(gameList, pcGameList, 'pc_id');
        // 找console的new 已关注过的游戏
        const consoleConfig = diffGameResult(
          gameList,
          consoleGameList,
          'console_id',
        );

        if (pcConfig?.newGame?.length || consoleConfig?.newGame?.length) {
          setHasSameSame(false);
        } else {
          // pc/console Benchmark在对应的follow分组下载相同或减少情况下
          // const pcGenreGameIdList = gameList?.map(item=> item?.pc_id)?.filter(Boolean);
          // const consoleGameIdList = gameList?.map(item=> item?.console_id)?.filter(Boolean);

          const pcGenreGameIdList = pcGameList?.map(item=> item?.app_id)?.filter(Boolean);
          const consoleGameIdList = consoleGameList?.map(item=> item?.app_id)?.filter(Boolean);
          
          const pcResult =
            pcConfig?.followedGame?.length > 0 &&
            pcConfig?.followedGame?.length <= pcGenreGameIdList?.length;

          const consoleResult =
            consoleConfig?.followedGame?.length > 0 &&
            consoleConfig?.followedGame?.length <= consoleGameIdList?.length;

          if (pcResult && consoleResult) {
            setHasSameSame(true);
          }else{
            setHasSameSame(false);
          }
          followedGamesIds.current.mobile = [];
          followedGamesIds.current.pc = pcGenreGameIdList || [];
          followedGamesIds.current.console = consoleGameIdList || [];
        }
        // pc 和 console新增游戏交集
        const newGameResult = getIntersection(
          pcConfig?.newGame,
          consoleConfig?.newGame,
          gameList,
        );
        // pc 和 console 已关注游戏交集
        const pcFollowId = pcConfig?.followedGame?.map((item) => item?.app_id);
        const pcFollow = gameList.filter((item) =>
          pcFollowId?.includes(item.pc_id),
        );
        const consoleFollowId = consoleConfig?.followedGame?.map(
          (item) => item?.app_id,
        );
        const consoleFollow = gameList.filter((item) =>
          consoleFollowId?.includes(item.console_id),
        );
        const followResultGameResult = getIntersection(
          pcFollow,
          consoleFollow,
          gameList,
        );
 
        setGenreGameMap({
          newGame: [
            ...newGameResult?.pc,
            ...newGameResult?.console,
            ...newGameResult?.combine,
          ],
          followedGame: [
            ...followResultGameResult?.pc,
            ...followResultGameResult?.console,
            ...followResultGameResult?.combine,
          ],
        });
        setHasGroupName(true);
      }
    }
  }, [groupList, entityName, genreConfig, gameList]);

  const diffGameResult = (
    genreGameList: any[],
    followGameList: any[],
    filed = 'id',
  ) => {
    const genreIdList = genreGameList?.map((item: any) => item?.[filed]);
    const followIdList = followGameList?.map((item: any) => item?.app_id);
    const followedGame = followGameList?.filter((item) =>
      genreIdList?.includes(item?.app_id),
    );
    const newGame = genreGameList?.filter(
      (item) => !followIdList?.includes(item?.[filed]) && item?.[filed],
    );
    return {
      newGame, // 新的游戏
      followedGame, // 关注过的游戏
    };
  };

  const getIntersection = (
    pcGame: any[],
    consoleGame: any[],
    allGame: BenchmarkGameItem[],
  ) => {
    const pcIdList = pcGame?.map((item) => item?.id);
    const consoleIdList = consoleGame?.map((item) => item?.id);
    const pcSet = new Set(pcIdList);
    const conSet = new Set(consoleIdList);
    const pcConsole = Array.from(pcSet).filter((x) => conSet?.has(x));
    const singePc = pcIdList?.filter((item) => !pcConsole?.includes(item));
    const singeConsole = consoleIdList?.filter(
      (item) => !pcConsole?.includes(item),
    );
    const formatterGame = (
      arr: string[],
      allGame: any[],
      platform: string[],
    ) => {
      return allGame
        ?.filter((item) => arr?.includes(item?.id))
        ?.map((item) => {
          return {
            ...item,
            platform,
          };
        });
    };
    return {
      pc: formatterGame(singePc, allGame, ['pc']),
      console: formatterGame(singeConsole, allGame, ['console']),
      combine: formatterGame(pcConsole, allGame, ['pc', 'console']),
    };
  };

  // 新建分组的名称
  const newGroupName = useMemo(() => {
    if (hasGroupName) {
      const copyList = groupList?.filter((item) =>
        item?.entity_name?.startsWith(
          `${entityName} - ${genreConfig?.name}-copy`,
        ),
      );
      if (!copyList?.length) {
        return `${entityName} - ${genreConfig?.name}-copy`;
      } else {
        const copyCountList = copyList?.map(
          (item) =>
            +item?.entity_name?.split(
              `${entityName} - ${genreConfig?.name}-copy`,
            )?.[1],
        );
        const targetList = copyCountList?.filter(Boolean);
        const max = targetList?.length ? Math.max(...targetList) + 1 : 1;
        return `${entityName} - ${genreConfig?.name}-copy${max}`;
      }
    }
    return `${entityName} - ${genreConfig?.name}`;
  }, [hasGroupName, groupList]);

  const goDetail = (type: 'mobile' | 'pc' | 'console', gameId: string) => {
    if (gameId) history.push(getDetailGameOverviewPath(gameId, type));
  };

  const columns: TableColumn[] = [
    {
      key: 'rank',
      width: 40,
      header: () => <div className="dbi-benchmark-follow-game-header">#</div>,
      render: (cvm) => {
        return (
          <div
            className={classnames({
              'dbi-benchmark-follow-game-mark': !cvm?.id,
            })}
          >
            {cvm?.rank}
          </div>
        );
      },
    },
    {
      key: 'game',
      width: 380,
      header: () => (
        <div className="dbi-benchmark-follow-game-header">
          {formatMessage({ id: 'dbi_game' })}
        </div>
      ),
      render: (cvm: GameType) => {
        return cvm?.id ? (
          <div className="dbi-benchmark-follow-game-colum">
            <div
              className="game-image-header"
              onClick={() =>
                goDetail(
                  cvm?.entity_type === 'mobile' ? cvm?.entity_type : 'pc',
                  cvm?.entity_type === 'mobile' ? cvm?.id : cvm?.pc_id,
                )
              }
            >
              <GameImage src={cvm?.cover} width={24} />
            </div>
            <div className="game-content">
              <span
                className="game-content-title"
                onClick={() =>
                  goDetail(
                    cvm?.entity_type === 'mobile' ? cvm?.entity_type : 'pc',
                    cvm?.entity_type === 'mobile' ? cvm?.id : cvm?.pc_id,
                  )
                }
              >
                {cvm?.entity_name}
              </span>
              <span>
                {cvm?.platform?.map((plat) => (
                  <a
                    className="game-content-plat-link"
                    key={plat}
                    target="_blank"
                    onClick={() =>
                      goDetail(
                        plat,
                        plat === 'mobile' ? cvm?.id : cvm?.[`${plat}_id`],
                      )
                    }
                  >
                    <SvgIcon
                      className="benchmark-gametype-icon"
                      type={GAME_ICON_MAP[plat]}
                    />
                  </a>
                ))}
              </span>
            </div>
          </div>
        ) : null;
      },
    },
  ];

  const handleClose = () => {
    setVisible(false);
  };

  const handleFollowClick = () => {
    if (loading) return;

    // follow中无关注的分组，即首次关注
    if (!hasGroupName) {
      setVisible(true);
      return;
    }

    // follow中对应的分组存在切游戏存在 benchmark游戏减少或相同的情况
    if (hasGroupName && hasSameGame) {
      Modal.confirm({
        message: formatMessage(
          { id: 'dbi_benchmark_group_exists' },
          { type: `${entityName} - ${genreConfig?.name || ''}` },
        ),
        description: formatMessage({ id: 'dbi_benchmark_group_exists_qus' }),
        okText: formatMessage({ id: 'dbi_confirm' }),
        cancelText: formatMessage({ id: 'dbi_cancel' }),
        onOk: () => {
          handleConfirm();
        },
        onCancel: () => {},
      });
      return;
    }

    // follow中对应的分组存在切游戏存在 benchmark游戏增加的情况
    if (hasGroupName && !hasSameGame) {
      setVisible(true);
    }
  };

  const handleConfirm = async (groupName?: string) => {
    const isNewGroupAction = !groupName;
    let game_company_ids = {};
    if (entity_type === 'mobile') {
      const idList = dataSource?.map((item) => item?.id)?.filter(Boolean);
      game_company_ids = {
        // mobile: idList,
        mobile: isNewGroupAction ? idList : uniq([...followedGamesIds.current.mobile, ...idList]),
      };
    }

    if (entity_type === 'combine') {
      const idMap = dataSource?.reduce(
        (result, item) => {
          result?.pc?.push(item?.pc_id || '');
          result?.console?.push(item?.console_id || '');
          return result;
        },
        { pc: [], console: [] } as { pc: string[]; console: string[] },
      );
      const pcIds = idMap?.pc?.filter(Boolean) || [];
      const consoleIds = idMap?.console?.filter(Boolean) || [];
      game_company_ids = {
        // pc: uniq(idMap?.pc?.filter(Boolean)),
        // console: uniq(idMap?.console?.filter(Boolean)),
        pc: isNewGroupAction ? uniq(pcIds) : uniq([...followedGamesIds.current.pc, ...pcIds]),
        console: isNewGroupAction ? uniq(consoleIds) : uniq([...followedGamesIds.current.console, ...consoleIds]),
      };
    }

    try {
      await followingCollectionGames({
        game_company_ids: game_company_ids,
        entity_types:
          entity_type === 'combine' ? ['pc', 'console'] : ['mobile'],
        is_collection: true,
        is_overwrite: true,
        group_name: groupName || newGroupName,
      });

      setVisible(false);

      const { destroy } = notification.success({
        unique: true,
        description: (
          <div>
            <span>{formatMessage({ id: 'dbi_benchmark_add_followed' })}</span>
            <span
              style={{ color: '#326FF0', cursor: 'pointer', marginLeft: 10 }}
              onClick={() => handleAddToGroup(destroy)}
            >
              {formatMessage({ id: 'dbi_benchmark_go_to_mange' })}
            </span>
          </div>
        ),
      });

      getCollectionGroupList?.()
    } catch (error) {
      console.log(error);
      // notification.error({
      //   unique: true,
      //   description: formatMessage({ id: 'dbi_failed_follow' }),
      // });
    }
  };

  const handleAddToGroup = (destroy: () => void) => {
    destroy?.();
    if (entity_type === 'mobile') {
      history.push('/intelligence/following/manage/mobile');
    }

    if (entity_type === 'combine') {
      history.push('/intelligence/following/manage/pc');
    }
  };


  const gameCount = useMemo(()=> {
    return hasGroupName? genreGameMap?.newGame?.length :dataSource?.length
  }, [hasGroupName, genreGameMap, dataSource])

  return (
    <>
      <Bubble
        dark
        content={formatMessage({ id: 'dbi_benchmark_follow_game_hover' })}
      >
        <span className={classnames([className, { 'is-disabled': loading }])} onClick={handleFollowClick}>
          {formatMessage({ id: 'dbi_benchmark_follow_game_genre' })}
        </span>
      </Bubble>
      <Modal
        visible={visible}
        size={540}
        onClose={handleClose}
        caption={formatMessage({ id: 'dbi_benchmark_follow_game_genre' })}
      >
        <Body>
          {loading ? (
            <div className="dbi_benchmark_follow_game_loading">
              <LoadingTip />
            </div>
          ) : dataSource?.length > 0 ? (
            <>
              <div className="dbi_benchmark_follow_game-tip">
                {formatMessage(
                  {
                    id: hasGroupName
                      ? 'dbi_benchmark_follow_game_tip'
                      : entity_type === 'mobile'
                      ? 'dbi_benchmark_follow_game_tip_mobile'
                      : 'dbi_benchmark_follow_game_tip_combine',
                  },
                  {
                    name: `${entityName} - ${genreConfig?.name || ''}`,
                    count: (
                      <span className="game-count">{hasGroupName?  genreGameMap?.newGame?.length :dataSource?.length}</span>
                    ),
                    target: gameCount <=1 ?  'game': 'games',
                  },
                )}
              </div>
              <Table
                className="dbi-benchmark-follow-game-table"
                records={dataSource}
                columns={columns}
                addons={[
                  scrollable({
                    virtualizedOptions: {
                      height: 348,
                      itemHeight: 48,
                    },
                  }),
                ]}
              />
            </>
          ) : (
            <div className="dbi-benchmark-follow-game-no-data">
              {formatMessage({ id: 'dbi_benchmark_no_data_yet' })}
            </div>
          )}
        </Body>

        <Footer>
          <Button
            type="primary"
            onClick={() =>
              handleConfirm(`${entityName} - ${genreConfig?.name || ''}`)
            }
            disabled={!dataSource?.length || loading}
          >
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          {hasGroupName && !hasSameGame && (
            <Button onClick={() => handleConfirm()}>
              {formatMessage({ id: 'dbi_benchmark_create_duplicate' })}
            </Button>
          )}
          <Button onClick={handleClose}>
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </Footer>
      </Modal>
    </>
  );
};

FollowGame.displayName = 'FollowGame';

export default FollowGame;
